import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { bookingsAPI, locationsAPI, seedData } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogOut, Plus, Calendar } from 'lucide-react';
import { getStatusColor, getSportColor } from '../../lib/utils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';
import { sortBookingsByPriority } from '../../lib/bookingSort';
import BottomNav from '../../components/BottomNav';

export default function HomePage() {
  const navigate = useNavigate();
  const { user, isGuest, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auth gate lives in <CustomerProtectedRoute /> (see App.js). By the
  // time HomePage renders, `user` is guaranteed non-null. We only need
  // to drive data loading from here.
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadData = async () => {
    // Don't load data if user is not authenticated
    if (!user) {
      return;
    }
    
    try {
      // Seed data if needed
      try {
        await seedData();
      } catch (e) {
        // Already seeded (409 / 400) — safe to ignore, but log for debuggability
        console.debug('seedData skipped:', e?.message || e);
      }

      // Load locations
      const locs = await locationsAPI.getLocations();
      setLocations(locs);

      // Load user bookings
      if (user) {
        if (isGuest && user.phone) {
          const userBookings = await bookingsAPI.getGuestBookings(user.phone);
          setBookings(userBookings);
        } else if (user.id) {
          const userBookings = await bookingsAPI.getUserBookings(user.id);
          setBookings(userBookings);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Don't show error toast if user is logged out (401/403 errors after logout)
      if (error.response && user) {
        // Only show error if user is still logged in
        const status = error.response.status;
        if (status !== 401 && status !== 403) {
          toast.error('Failed to load data');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    // Use setTimeout to avoid React concurrent rendering error
    setTimeout(() => {
      navigate('/');
    }, 0);
  };

  // Sort by status priority (AWAITING_PAYMENT first) so the customer always
  // sees actionable bookings before completed/cancelled ones — even if older.
  const recentBookings = sortBookingsByPriority(bookings).slice(0, 3);

  // Return null if user is not loaded yet to prevent rendering errors
  if (!user) {
    return null;
  }

  return (
    <div className="mobile-container min-h-screen pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-black font-heading uppercase tracking-tight">
              SmashZone
            </h1>
            <p className="text-sm mt-1 opacity-90">
              {user ? (isGuest ? `Guest: ${user.phone}` : user.name) : 'Loading...'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleLogout}
            data-testid="logout-button"
            className="text-white hover:bg-white/20"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* CTA Section */}
      <div className="p-6 space-y-4">
        <Button
          onClick={() => navigate('/book')}
          data-testid="book-now-button"
          className="w-full btn-volt h-16 text-lg"
        >
          <Plus className="w-6 h-6 mr-2" />
          Book Now
        </Button>
      </div>

      {/* Recent Bookings */}
      <div className="px-6 pb-6" data-testid="recent-bookings-section">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold font-heading uppercase">My Bookings</h2>
          {bookings.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/bookings')}
              data-testid="view-all-bookings-button"
              className="text-primary font-bold uppercase text-xs"
            >
              View All
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : bookings.length === 0 ? (
          <Card className="p-8 text-center glass-card" data-testid="no-bookings-message">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No bookings yet</p>
            <p className="text-sm text-muted-foreground mt-1">Tap "Book Now" to get started!</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {recentBookings.map((booking) => (
              <Card
                key={booking.id}
                onClick={() => navigate(`/booking/${booking.id}`)}
                data-testid="booking-card"
                className="p-4 glass-card hover:border-accent/50 transition-all cursor-pointer court-card"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`status-badge ${getStatusColor(booking.status)}`}>
                        {booking.status.replace('_', ' ')}
                      </span>
                    </div>
                    <DateTimeDisplay
                      date={booking.booking_date}
                      timeSlot={booking.time_slot}
                      duration={booking.duration}
                      className="mt-1"
                      testId={`home-booking-${booking.id}`}
                    />
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-xl">${(booking.status === 'PENDING_ALLOCATION'
                      ? (booking.estimated_total ?? booking.final_total ?? 0)
                      : (booking.final_total ?? booking.estimated_total ?? 0)
                    ).toFixed(2)}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  {booking.courts_requested && booking.courts_requested.length > 0 ? (
                    booking.courts_requested.map((court, idx) => (
                      <span key={`${court.sport}-${idx}`} className={`sport-badge ${getSportColor(court.sport)}`}>
                        {court.sport}
                      </span>
                    ))
                  ) : (
                    <span className="sport-badge bg-gray-100 text-gray-600">
                      {booking.sport || 'N/A'}
                    </span>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}